import { BsPersonCircle } from "react-icons/bs";
import { Popover, message, AutoComplete, Input } from "antd";
import { UserOutlined, LogoutOutlined, SearchOutlined } from '@ant-design/icons'
import { Link, useNavigate } from "react-router-dom";
import { userStore } from "../../store/userStore";
import { useLogout } from "../../hooks/auth/authentication";
import { useState, useEffect } from "react";
import { ADMIN, FINAL_ACCOUNT, hasPayrollAccess } from "../../helpers/roles";

const getAllSearchOptions = (userRoles = []) => [
    // Always available
    { value: '/dashboard', label: 'Dashboard', category: 'Navigation' },
    { value: '/dashboard/profile', label: 'My Profile', category: 'Account' },
    { value: '/dashboard/memo/manage/memo', label: 'Memo System', category: 'Documents' },
    { value: '/dashboard/memo/recommendation', label: 'Memo Recommendations', category: 'Documents' },
    { value: '/dashboard/voucher', label: 'Payment Vouchers', category: 'Financial' },
    { value: '/dashboard/payroll', label: 'Monthly Payroll Records', category: 'Payroll' },
    { value: '/dashboard/report', label: 'Reports', category: 'Reports' },
    
    // Admin only
    ...(userRoles?.includes(ADMIN) ? [
        { value: '/dashboard/users', label: 'Manage Users', category: 'User Management' },
        { value: '/dashboard/department', label: 'Departments/Faculties', category: 'Administration' },
        { value: '/dashboard/unit', label: 'Units', category: 'Administration' },
        { value: '/dashboard/voucher-config', label: 'Salary Structure', category: 'Configuration' },
        { value: '/dashboard/budget-category', label: 'Account Type Category', category: 'Configuration' },
        { value: '/dashboard/subaccount-type', label: 'Account Type', category: 'Configuration' },
        { value: '/dashboard/budget-approval', label: 'Budget Approval Stages', category: 'Configuration' },
        { value: '/dashboard/financial-year', label: 'Financial Year', category: 'Configuration' },
        { value: '/dashboard/staff-category', label: 'Staff Category', category: 'Configuration' },
        { value: '/dashboard/designation', label: 'Designations', category: 'Configuration' },
        { value: '/dashboard/voucher-approval', label: 'Voucher Approval', category: 'Configuration' },
        { value: '/dashboard/tax-config', label: 'Taxes', category: 'Configuration' },
        { value: '/dashboard/level', label: 'Level', category: 'Configuration' },
        { value: '/dashboard/step', label: 'Step', category: 'Configuration' },
        { value: '/dashboard/store', label: 'Stores', category: 'Inventory' },
        { value: '/dashboard/store-department', label: 'Store Departments', category: 'Inventory' },
        { value: '/dashboard/category', label: 'Store Categories', category: 'Inventory' },
        { value: '/dashboard/item', label: 'Store Items', category: 'Inventory' },
        { value: '/dashboard/store-transactions', label: 'Store Transactions', category: 'Inventory' },
    ] : []),
    
    // Budget access
    ...(userRoles?.includes(ADMIN) || userRoles?.includes("BUDGET_OFFICER") || userRoles?.includes("CREATE_BUDGET") ? [
        { value: '/dashboard/budgets', label: 'Manage Budget', category: 'Financial' },
        { value: '/dashboard/staff-projection', label: 'Staff Projection', category: 'Financial' },
    ] : []),
    
    // Final Account access
    ...(userRoles?.includes(FINAL_ACCOUNT) ? [
        { value: '/dashboard/final-accounts', label: 'Final Accounts', category: 'Financial' },
    ] : []),
    
    // Payroll access
    ...(hasPayrollAccess(userRoles) ? [
        { value: '/dashboard/payroll-config', label: 'Manage Payrolls', category: 'Payroll' },
        { value: '/dashboard/payroll-approval', label: 'Payroll Approval', category: 'Payroll' },
        { value: '/dashboard/benefit', label: 'Benefits', category: 'Payroll' },
        { value: '/dashboard/deduction', label: 'Deductions', category: 'Payroll' },
    ] : []),
];

const GlobalSearch = () => {
    const [searchValue, setSearchValue] = useState('');
    const [isVisible, setIsVisible] = useState(false);
    const navigate = useNavigate();
    const user = userStore(state => state.user);

    const searchOptions = getAllSearchOptions(user?.roles);

    const filteredOptions = searchOptions.filter(option =>
        option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.category.toLowerCase().includes(searchValue.toLowerCase())
    ).map(option => ({
        value: option.value,
        label: (
            <div className="search-option">
                <div className="search-option-title">{option.label}</div>
                <div className="search-option-category">{option.category}</div>
            </div>
        ),
        key: option.value
    }));

    const handleSelect = (value) => {
        navigate(value);
        setSearchValue('');
        setIsVisible(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsVisible(false);
            setSearchValue('');
        }
    };

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setIsVisible(true);
                setTimeout(() => {
                    const searchInput = document.querySelector('.global-search input');
                    if (searchInput) searchInput.focus();
                }, 100);
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, []);

    return (
        <div className="global-search">
            <div className="search-wrapper">
                <SearchOutlined className="search-prefix-icon" />
                <AutoComplete
                    options={filteredOptions}
                    onSelect={handleSelect}
                    onSearch={setSearchValue}
                    value={searchValue}
                    open={isVisible && searchValue.length > 0}
                    onFocus={() => setIsVisible(true)}
                    onBlur={() => setTimeout(() => setIsVisible(false), 200)}
                    placeholder="Search features... (Ctrl+K)"
                    allowClear
                    onKeyDown={handleKeyDown}
                    className="search-autocomplete"
                />
            </div>
        </div>
    );
};

const UserMenu = () => {
    const removeUserSession = userStore(state=>state.logout);
    const navigate = useNavigate();

    const logout = useLogout();
    const handleLogout = () => {
        const success = logout();
        removeUserSession();
        if(!success) {
            return;
        }
        message.success("Logout successful, Redirecting...");
        setTimeout(() => navigate("/"), 1000);
    }

    return (
        <div className="user-menu">
            <Link to='/dashboard/profile' className="user-menu-item">
                <UserOutlined className="user-menu-icon" />
                <span>Profile</span>
            </Link>
            <div className="user-menu-divider" />
            <button onClick={handleLogout} className="user-menu-item logout-button">
                <LogoutOutlined className="user-menu-icon" />
                <span>Logout</span>
            </button>
        </div>
    );
}

export default function Header() {
    const user = userStore(state=>state.user);

    return (
        <div className="nav">
            <div className="nav-user">
                <h2>{user.name}</h2>
                <span className="nav-user-role">Administrator</span>
            </div>
            <div className="nav-center">
                <GlobalSearch />
            </div>
            <div className="nav-actions">
                <Popover
                    content={<UserMenu />}
                    trigger="click"
                    placement="bottomRight"
                    overlayClassName="user-menu-popover"
                >
                    <button className="nav-user-button">
                        <BsPersonCircle className="nav-user-icon" />
                    </button>
                </Popover>
            </div>
        </div>
    );
}
